import { useCallback, useMemo, useState } from "react";
import { resolveTopicClusteringLens } from "@okf/ootils";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { usePeriodSelection } from "./periodSelection";
import { buildSignalCategoryTheme } from "@/components/analysisModules/comps/SignalFilters/signalCategories";

/**
 * The scope a Priorities surface with no Topics sidebar scores over (the
 * Dashboard's New tab): the tenant's lens, the whole corpus (no filters),
 * the shared period selection (the period, and what it is compared with) and the signal meta + category
 * theme remembered from the counts response. Every surface on the page that
 * calls usePrioritiesScoring with this scope shares its requests — same
 * inputs, same react-query keys, one round trip each.
 *
 * Returns { topicClusteringLens, period, comparePeriod, compareKey, periodLoading, dateFieldMap,
 *           historyFilters, signalMetaById, onCountsMeta, theme }.
 *
 * `period` is null both while the period list is loading and when the
 * tenant has no dated observation (nothing annotated yet); `periodLoading`
 * tells the two apart, so a surface shows a skeleton for the first and an
 * empty state for the second.
 */
const NO_FILTERS = [];

export const usePrioritiesDashboardScope = () => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const topicClusteringLens = resolveTopicClusteringLens({ platformConfigs_ai: platformConfigs?.ai });

  const { period, comparePeriod, compareKey, periodLoading, dateFieldMap } = usePeriodSelection();

  const [signalMetaById, setSignalMetaById] = useState({});
  const [signalConfig, setSignalConfig] = useState({ signalCategories: null, prioritiesCopy: null });
  const onCountsMeta = useCallback((payload) => {
    if (payload?.signalMetaById) setSignalMetaById(payload.signalMetaById);
    if (payload?.signalCategories) setSignalConfig({ signalCategories: payload.signalCategories, prioritiesCopy: payload.prioritiesCopy || null });
  }, []);
  const theme = useMemo(
    () => buildSignalCategoryTheme(signalConfig.signalCategories, { prioritiesCopy: signalConfig.prioritiesCopy }),
    [signalConfig]
  );

  return {
    topicClusteringLens,
    period,
    comparePeriod,
    compareKey,
    periodLoading,
    dateFieldMap,
    historyFilters: NO_FILTERS,
    signalMetaById,
    onCountsMeta,
    theme,
  };
};
